import React, { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import ZoomVideo from '@zoom/videosdk';
import PageWrap from 'components/PageWrap';
import ZoomContext from 'contexts/ZoomContext';
import ZoomVideoOutlet from './ZoomVideoOutlet';
import { toast } from 'components/Toast';

import query from 'graphql/query';
import { CONF_ZOOM_KEY, CONF_ZOOM_SECRET } from 'utils/config';
import { fullName } from 'utils/helpers';
import { b64DecodeUnicode, generateVideoToken } from 'utils/zoomHelpers';
import { IConsultationDetails } from 'utils/models';

const zmClient = ZoomVideo.createClient();

const ZoomVideoLayout: FC = () => {
  const { id } = useParams();
  const [isError, setIsError] = useState(false);
  const [meetingArgs, setMeetingArgs] = useState<any>(null);

  const { data: dataConsultation } = useQuery<{
    consultationDetails: {
      code: string;
      success: boolean;
      message: string;
      consultation: IConsultationDetails;
    };
  }>(query.consultationDetails, {
    variables: { id },
    fetchPolicy: 'network-only'
  });

  const consultationDetails = dataConsultation?.consultationDetails.consultation;

  useEffect(() => {
    const script = document.createElement('script');
    script.src = '/coi-serviceworker.js';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (dataConsultation?.consultationDetails.success === false) {
      toast({
        title: 'Error',
        message: 'You are not authorized to make this consultation',
        variant: 'error'
      });
      setIsError(true);
    }
  }, [dataConsultation]);

  useEffect(() => {
    if (!consultationDetails) return;

    const baseArgs = Object.fromEntries(new URLSearchParams(window.location.search));

    const decodedArgs = { ...baseArgs };
    if (baseArgs.web && baseArgs.web !== '0') {
      ['topic', 'name', 'password', 'sessionKey', 'userIdentity'].forEach((field) => {
        if (Object.hasOwn(baseArgs, field)) {
          try {
            decodedArgs[field] = b64DecodeUnicode(baseArgs[field]);
          } catch {
            console.log('Ignoring base64 decode error on', field);
          }
        }
      });
    }

    const role = parseInt(decodedArgs.role || '1', 10);

    [
      'enforceGalleryView',
      'enforceVB',
      'cloud_recording_option',
      'cloud_recording_election'
    ].forEach((field) => {
      if (decodedArgs[field] !== undefined) {
        //@ts-ignore
        decodedArgs[field] = Number(decodedArgs[field]) || 0;
      }
    });

    const finalArgs = {
      sdkKey: CONF_ZOOM_KEY,
      sdkSecret: CONF_ZOOM_SECRET,
      webEndpoint: 'zoom.us',
      topic: `session-${consultationDetails.reference_id}-${id}`,
      name: `Dr. ${fullName(consultationDetails.doctor || null)}`,
      password: '',
      sessionKey: decodedArgs.sessionKey || '',
      userIdentity: decodedArgs.userIdentity || '',
      role,
      enforceGalleryView: decodedArgs.enforceGalleryView || 0,
      enforceVB: decodedArgs.enforceVB || 0,
      cloud_recording_option: decodedArgs.cloud_recording_option || '',
      cloud_recording_election: decodedArgs.cloud_recording_election || '',
      telemetry_tracking_id: decodedArgs.telemetry_tracking_id
        ? b64DecodeUnicode(decodedArgs.telemetry_tracking_id)
        : '',
      web: '1'
    };

    // @ts-ignore
    finalArgs.signature = generateVideoToken(
      // @ts-ignore
      finalArgs.sdkKey,
      finalArgs.sdkSecret,
      finalArgs.topic,
      finalArgs.sessionKey,
      finalArgs.userIdentity,
      finalArgs.role,
      finalArgs.cloud_recording_option,
      finalArgs.cloud_recording_election,
      finalArgs.telemetry_tracking_id
    );

    console.log('✅ Final meetingArgs:', finalArgs);

    setMeetingArgs(finalArgs);
  }, [consultationDetails, id]);

  if (!meetingArgs) {
    return (
      <PageWrap title="On going Consultation">
        <div className="h-screen flex items-center justify-center text-gray-600">
          Preparing session...
        </div>
      </PageWrap>
    );
  }

  return (
    <PageWrap title="On going Consultation">
      <main className="flex flex-col h-screen bg-hazel-bone-100 relative">
        <ZoomContext.Provider value={zmClient}>
          <ZoomVideoOutlet
            meetingArgs={meetingArgs}
            consultationDetails={consultationDetails}
            isError={isError}
          />
        </ZoomContext.Provider>
      </main>
    </PageWrap>
  );
};

export default ZoomVideoLayout;
